import { useId, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Download, Ellipsis, Eye, FileText, Globe, Pencil, Trash2, Undo2 } from 'lucide-react';
import { adminQueries, getErrorMessage } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { formatDate, formatDateTime, formatFileSize, formatNumber } from '@blimo/shared';
import {
  applyProblemToForm,
  Badge,
  Button,
  Checkbox,
  cn,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DocumentViewer,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorState,
  Form,
  FormRootError,
  InputField,
  Label,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextareaField,
  toast,
} from '@blimo/ui';

const PREVIEWABLE = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'image/webp'];
/** Inline-Ansicht liefert der Server nur für PDF/Bilder (sonst Download). @param {any} doc */
const isPreviewable = (doc) => PREVIEWABLE.includes(doc?.mime_type);

/** @param {any} doc */
const displayName = (doc) => doc.title || doc.original_name || `Dokument #${doc.id}`;

/**
 * Dokumente eines Ordners als Tabelle mit Aktionen (Vorschau, Download, Bearbeiten, Veröffentlichen/Zurücknehmen, Löschen).
 * @param {{ folder: any, ownerType: 'property'|'unit'|'tenancy'|'global', can: { upload: boolean, publish: boolean, delete: boolean }, onChanged: () => unknown }} props
 */
export function FolderDocuments({ folder, ownerType, can, onChanged }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const folderQuery = adminQueries.folderDocuments(api, folder.id);
  const query = useQuery(folderQuery);
  const [moving, setMoving] = useState(false);
  const [preview, setPreview] = useState(/** @type {any} */ (null));
  const [editing, setEditing] = useState(/** @type {any} */ (null));
  const [publishing, setPublishing] = useState(/** @type {any} */ (null));
  const [unpublishing, setUnpublishing] = useState(/** @type {any} */ (null));
  const [deleting, setDeleting] = useState(/** @type {any} */ (null));

  if (query.error) return <ErrorState compact error={query.error} onRetry={() => query.refetch()} />;
  if (query.isPending) return <Skeleton className="h-32 w-full" />;
  const documents = query.data ?? [];

  /** F-407: move one document up/down; the server stores the order (also used by the portal). @param {number} index @param {-1|1} delta */
  async function move(index, delta) {
    const ids = documents.map((d) => d.id);
    const target = index + delta;
    if (target < 0 || target >= ids.length) return;
    [ids[index], ids[target]] = [ids[target], ids[index]];
    setMoving(true);
    try {
      queryClient.setQueryData(folderQuery.queryKey, await api.admin.documentFolders.reorder(folder.id, ids));
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Reihenfolge konnte nicht gespeichert werden.'));
      await query.refetch();
    } finally {
      setMoving(false);
    }
  }

  async function unpublish() {
    try {
      await api.admin.documentFolders.unpublish(folder.id, unpublishing.id);
      toast.success('Veröffentlichung zurückgenommen');
      await onChanged();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Die Veröffentlichung konnte nicht zurückgenommen werden.'));
    }
  }
  async function remove() {
    try {
      await api.admin.documents.remove(deleting.id);
      toast.success('Dokument gelöscht');
      if (preview?.id === deleting.id) setPreview(null);
      await onChanged();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Das Dokument konnte nicht gelöscht werden.'));
    }
  }

  return (
    <>
      {documents.length === 0 ? (
        <EmptyState
          compact
          icon={FileText}
          title="Keine Dokumente in diesem Ordner"
          description={can.upload ? 'Dateien oben hineinziehen oder auswählen.' : undefined}
        />
      ) : (
        <Table aria-label={`Dokumente in ${folder.name}`}>
          <TableHeader>
            <TableRow>
              <TableHead className="w-full">Dokument</TableHead>
              <TableHead className="hidden text-right md:table-cell">Größe</TableHead>
              <TableHead className="hidden sm:table-cell">Hochgeladen</TableHead>
              <TableHead>Portal</TableHead>
              <TableHead className="w-0">
                <span className="sr-only">Aktionen</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc, index) => (
              <DocumentRow
                key={doc.id}
                doc={doc}
                can={can}
                order={
                  can.upload && documents.length > 1
                    ? {
                        first: index === 0,
                        last: index === documents.length - 1,
                        disabled: moving,
                        onUp: () => move(index, -1),
                        onDown: () => move(index, 1),
                      }
                    : null
                }
                onPreview={() => setPreview(doc)}
                onEdit={() => setEditing(doc)}
                onPublish={() => setPublishing(doc)}
                onUnpublish={() => setUnpublishing(doc)}
                onDelete={() => setDeleting(doc)}
              />
            ))}
          </TableBody>
        </Table>
      )}

      <Sheet open={Boolean(preview)} onOpenChange={(open) => !open && setPreview(null)}>
        <SheetContent className="sm:max-w-3xl">
          {preview && (
            <>
              <SheetHeader>
                <SheetTitle className="truncate">{displayName(preview)}</SheetTitle>
                <SheetDescription>
                  {[preview.original_name, preview.size != null ? formatFileSize(preview.size) : null].filter(Boolean).join(' · ')}
                </SheetDescription>
              </SheetHeader>
              <div className="px-4 pb-4">
                <DocumentViewer
                  title={displayName(preview)}
                  mimeType={preview.mime_type}
                  src={api.admin.documents.downloadUrl(preview.id, { inline: true })}
                  downloadHref={api.admin.documents.downloadUrl(preview.id)}
                />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {can.upload && (
        <Sheet open={Boolean(editing)} onOpenChange={(open) => !open && setEditing(null)}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Dokument bearbeiten</SheetTitle>
              <SheetDescription>{editing?.original_name}</SheetDescription>
            </SheetHeader>
            {editing && <DocumentForm folder={folder} doc={editing} onDone={() => setEditing(null)} onSaved={onChanged} />}
          </SheetContent>
        </Sheet>
      )}

      {can.publish && (
        <>
          <Dialog open={Boolean(publishing)} onOpenChange={(open) => !open && setPublishing(null)}>
            <DialogContent>
              {publishing && <PublishForm folder={folder} doc={publishing} ownerType={ownerType} onDone={() => setPublishing(null)} onPublished={onChanged} />}
            </DialogContent>
          </Dialog>
          <ConfirmDialog
            open={Boolean(unpublishing)}
            onOpenChange={(open) => !open && setUnpublishing(null)}
            title="Veröffentlichung zurücknehmen?"
            description={unpublishing ? `„${displayName(unpublishing)}“ ist danach im Portal nicht mehr sichtbar.` : undefined}
            confirmLabel="Zurücknehmen"
            onConfirm={unpublish}
          />
        </>
      )}

      {can.delete && (
        <ConfirmDialog
          open={Boolean(deleting)}
          onOpenChange={(open) => !open && setDeleting(null)}
          title="Dokument löschen?"
          description={
            deleting
              ? `„${displayName(deleting)}“ wird gelöscht und ist danach weder in der Verwaltung noch im Portal abrufbar – auch nicht in anderen Ordnern, in denen es verknüpft ist.`
              : undefined
          }
          affectedCount={1}
          confirmLabel="Löschen"
          destructive
          onConfirm={remove}
        />
      )}
    </>
  );
}

/**
 * @param {{ doc: any, can: any, order: { first: boolean, last: boolean, disabled: boolean, onUp: () => void, onDown: () => void }|null,
 *   onPreview: () => void, onEdit: () => void, onPublish: () => void, onUnpublish: () => void, onDelete: () => void }} props
 */
function DocumentRow({ doc, can, order, onPreview, onEdit, onPublish, onUnpublish, onDelete }) {
  const api = useApi();
  const name = displayName(doc);
  const published = Boolean(doc.published_at);
  const hasMenu = can.upload || can.publish || can.delete;
  return (
    <TableRow>
      <TableCell className="w-full max-w-0 whitespace-normal">
        <div className="flex min-w-0 items-start gap-2">
          <FileText className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="flex min-w-0 flex-col">
            {isPreviewable(doc) ? (
              <button type="button" onClick={onPreview} className="break-words text-left sm:truncate font-medium text-primary hover:underline">
                {name}
              </button>
            ) : (
              <span className="break-words font-medium sm:truncate">{name}</span>
            )}
            {doc.original_name && doc.original_name !== name && (
              <span className="break-all text-xs sm:truncate text-muted-foreground">{doc.original_name}</span>
            )}
            {doc.description && <span className="line-clamp-2 text-xs text-muted-foreground">{doc.description}</span>}
            <span className="text-xs text-muted-foreground sm:hidden">
              {[formatDate(doc.created_at), doc.size != null ? formatFileSize(doc.size) : null].filter(Boolean).join(' · ')}
            </span>
          </div>
        </div>
      </TableCell>
      <TableCell className="numeric hidden text-right md:table-cell">{doc.size != null ? formatFileSize(doc.size) : '–'}</TableCell>
      <TableCell className="hidden sm:table-cell">
        <div className="flex flex-col">
          <span className="numeric">{formatDateTime(doc.created_at)}</span>
          {doc.uploaded_by?.name && <span className="text-xs text-muted-foreground">{doc.uploaded_by.name}</span>}
        </div>
      </TableCell>
      <TableCell>
        {published ? (
          <div className="flex flex-col items-start gap-0.5">
            <Badge variant="success">Veröffentlicht</Badge>
            <span className="numeric text-xs text-muted-foreground">{formatDate(doc.published_at)}</span>
          </div>
        ) : (
          <Badge variant="neutral">Nicht veröffentlicht</Badge>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-end gap-0.5">
          {order && (
            <>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={order.onUp}
                disabled={order.first || order.disabled}
                aria-label={`${name} nach oben`}
                title="Nach oben"
              >
                <ArrowUp aria-hidden="true" />
              </Button>
              <Button
                size="icon-sm"
                variant="ghost"
                onClick={order.onDown}
                disabled={order.last || order.disabled}
                aria-label={`${name} nach unten`}
                title="Nach unten"
              >
                <ArrowDown aria-hidden="true" />
              </Button>
            </>
          )}
          {isPreviewable(doc) && (
            <Button size="icon-sm" variant="ghost" onClick={onPreview} aria-label={`${name} ansehen`} title="Vorschau">
              <Eye aria-hidden="true" />
            </Button>
          )}
          <Button asChild size="icon-sm" variant="ghost">
            <a href={api.admin.documents.downloadUrl(doc.id)} download aria-label={`${name} herunterladen`} title="Herunterladen">
              <Download aria-hidden="true" />
            </a>
          </Button>
          {hasMenu && (
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button size="icon-sm" variant="ghost" aria-label={`Aktionen für ${name}`} title="Weitere Aktionen">
                  <Ellipsis aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {can.upload && (
                  <DropdownMenuItem onSelect={onEdit}>
                    <Pencil aria-hidden="true" /> Titel & Beschreibung
                  </DropdownMenuItem>
                )}
                {can.publish &&
                  (published ? (
                    <DropdownMenuItem onSelect={onUnpublish}>
                      <Undo2 aria-hidden="true" /> Veröffentlichung zurücknehmen
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onSelect={onPublish}>
                      <Globe aria-hidden="true" /> Veröffentlichen
                    </DropdownMenuItem>
                  ))}
                {can.delete && (
                  <>
                    {(can.upload || can.publish) && <DropdownMenuSeparator />}
                    <DropdownMenuItem variant="destructive" onSelect={onDelete}>
                      <Trash2 aria-hidden="true" /> Löschen
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

const documentSchema = z.object({
  title: z.string().trim().max(255, 'Höchstens 255 Zeichen.'),
  description: z.string().trim().max(5000, 'Höchstens 5000 Zeichen.'),
});

function DocumentForm({ folder, doc, onDone, onSaved }) {
  const api = useApi();
  // Die API liefert als Titel den Dateinamen, wenn kein eigener Titel gesetzt ist.
  const ownTitle = doc.title && doc.title !== doc.original_name ? doc.title : '';
  const form = useForm({ resolver: zodResolver(documentSchema), defaultValues: { title: ownTitle, description: doc.description ?? '' } });
  const submit = form.handleSubmit(async (values) => {
    try {
      await api.admin.documentFolders.updateDocument(folder.id, doc.id, { title: values.title.trim() || null, description: values.description.trim() || null });
      toast.success('Dokument gespeichert');
      await onSaved();
      onDone();
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['title', 'description'] });
    }
  });
  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex flex-1 flex-col gap-4 px-4">
        <FormRootError />
        <InputField name="title" label="Titel" placeholder={doc.original_name} autoComplete="off" description="Leer lassen, um den Dateinamen anzuzeigen." />
        <TextareaField name="description" label="Beschreibung" rows={4} />
        <SheetFooter className="px-0">
          <Button type="submit" loading={form.formState.isSubmitting}>
            Speichern
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}

/** Veröffentlichen mit Bestätigung; bei Liegenschafts-Ordnern optional Eigentümer benachrichtigen (+ Anhang). */
function PublishForm({ folder, doc, ownerType, onDone, onPublished }) {
  const api = useApi();
  const [notify, setNotify] = useState(false);
  const [attach, setAttach] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));
  const notifyId = useId();
  const attachId = useId();
  const canNotify = ownerType === 'property';

  async function confirm() {
    setPending(true);
    setError(null);
    try {
      const result = await api.admin.documentFolders.publish(folder.id, doc.id, canNotify && notify ? { notify_owners: true, attach } : {});
      toast.success(canNotify && notify ? `Veröffentlicht · ${formatNumber(result?.notified ?? 0)} Eigentümer benachrichtigt` : 'Dokument veröffentlicht');
      await onPublished();
      onDone();
    } catch (e) {
      setError(getErrorMessage(e, 'Das Dokument konnte nicht veröffentlicht werden.'));
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>Dokument veröffentlichen?</DialogTitle>
        <DialogDescription>
          „{displayName(doc)}“ wird im Portal sichtbar
          {folder.visible_in_portal ? '.' : ', sobald der Ordner „' + folder.name + '“ im Portal eingeblendet ist.'}
        </DialogDescription>
      </DialogHeader>
      {canNotify && (
        <div className="flex flex-col gap-2 text-sm">
          <span className="flex items-center gap-2">
            <Checkbox id={notifyId} checked={notify} onChange={(e) => setNotify(e.target.checked)} />
            <Label htmlFor={notifyId} className="font-normal">
              Eigentümer per E-Mail benachrichtigen
            </Label>
          </span>
          <span className="flex items-center gap-2">
            <Checkbox id={attachId} checked={attach} disabled={!notify} onChange={(e) => setAttach(e.target.checked)} />
            <Label htmlFor={attachId} className={cn('font-normal', !notify && 'text-muted-foreground')}>
              Datei an E-Mail anhängen
            </Label>
          </span>
          {notify && (
            <p className="text-xs text-muted-foreground">Alle aktuellen Eigentümer der Liegenschaft, je E-Mail-Adresse einmal; nicht im Vorbereitungsmodus.</p>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onDone} disabled={pending}>
          Abbrechen
        </Button>
        <Button type="button" onClick={confirm} loading={pending}>
          Veröffentlichen
        </Button>
      </DialogFooter>
    </div>
  );
}
